import { useRef, useEffect, useState } from "react";
import { DatePicker } from "@rio-cloud/rio-uikit";
import Card from "@rio-cloud/rio-uikit/Card";
import moment, { Moment } from "moment";
import { DATE_LOCALE } from "../../../utils/dateRange";

interface DateRangePickerProps {
  show: boolean;
  onClose: () => void;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
}

export default function DateRangePicker({
  show,
  onClose,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: DateRangePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [startInputValue, setStartInputValue] = useState("");
  const [endInputValue, setEndInputValue] = useState("");
  const [hasEndDatePickerError, setHasEndDatePickerError] = useState(false);

  const formatDateForDisplay = (date?: string) => {
    if (!date) return "";
    return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
  };

  const formatDateAsTyping = (value: string) => {
    const onlyDigits = value.replace(/\D/g, "").slice(0, 8);

    if (onlyDigits.length <= 2) return onlyDigits;
    if (onlyDigits.length <= 4) {
      return `${onlyDigits.slice(0, 2)}/${onlyDigits.slice(2)}`;
    }

    return `${onlyDigits.slice(0, 2)}/${onlyDigits.slice(2, 4)}/${onlyDigits.slice(4)}`;
  };

  const parseTypedDate = (value: string) => {
    if (value.length !== 10) return null;

    const parsedDate = moment(value, "DD/MM/YYYY", true);
    return parsedDate.isValid() ? parsedDate : null;
  };

  useEffect(() => {
    if (show) {
      setStartInputValue(formatDateForDisplay(startDate));
      setEndInputValue(formatDateForDisplay(endDate));
    }
  }, [show, startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        const monthNavigation = target.closest(".month-navigation");
        if (!monthNavigation) {
          onClose();
        }
      }
    };

    if (show) {
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [show, onClose]);

  const handleStartDateChange = (result: Moment | string) => {
    if (typeof result === "string") {
      const formattedInput = formatDateAsTyping(result);
      setStartInputValue(formattedInput);

      if (!formattedInput) {
        setEndInputValue("");
        return;
      }

      const typedDate = parseTypedDate(formattedInput);

      if (!typedDate) return;

      const start = typedDate.clone();
      const startFormatted = start.format("YYYY-MM-DD");
      const endOfMonth = start.clone().endOf("month").format("YYYY-MM-DD");

      setEndInputValue(start.clone().endOf("month").format("DD/MM/YYYY"));
      onStartDateChange?.(startFormatted);
      onEndDateChange?.(endOfMonth);
      return;
    }

    if (!result.isValid()) return;

    const start = result.clone();
    const startFormatted = start.format("YYYY-MM-DD");

    const endOfMonth = start.clone().endOf("month").format("YYYY-MM-DD");

    setStartInputValue(start.format("DD/MM/YYYY"));
    setEndInputValue(start.clone().endOf("month").format("DD/MM/YYYY"));
    onStartDateChange?.(startFormatted);
    onEndDateChange?.(endOfMonth);
  };

  const handleEndDateChange = (result: Moment | string) => {
    if (typeof result === "string") {
      const formattedInput = formatDateAsTyping(result);
      setEndInputValue(formattedInput);

      if (!formattedInput) {
        return;
      }

      const typedDate = parseTypedDate(formattedInput);
      if (!typedDate) return;

      const date = typedDate.format("YYYY-MM-DD");

      if (startDate && typedDate.isBefore(moment(startDate, "YYYY-MM-DD"), "day")) {
        setEndInputValue("");
        setHasEndDatePickerError(true)
        return
      }

       if (!isRangeDataValid(typedDate)) {
        setEndInputValue("");
        setHasEndDatePickerError(true)
        return
      }

      setHasEndDatePickerError(false)
      onEndDateChange?.(date);
      if (startDate && date) {
        onClose();
      }
      return;
    }

    if (!result.isValid()) return;

    const selectedDate = result.clone();
    const date = selectedDate.format("YYYY-MM-DD");

    if (startDate && selectedDate.isBefore(moment(startDate, "YYYY-MM-DD"), "day")) {
      setEndInputValue("");
      setHasEndDatePickerError(true)
      return
    }

    if (!isRangeDataValid(selectedDate)) {
      setEndInputValue("");
      setHasEndDatePickerError(true)
      return
    }

    setEndInputValue(selectedDate.format("DD/MM/YYYY"));
    onEndDateChange?.(date);
    setHasEndDatePickerError(false)

    if (startDate && date) {
      onClose();
    }
  };

  const isRangeDataValid = (selectedDate: Moment) => {
    if (selectedDate && startDate) {
      const minDate = moment(startDate);
      const maxDate = moment(startDate).add(5, "year");

      return selectedDate.isSameOrAfter(minDate, "day") && selectedDate.isSameOrBefore(maxDate, "day");
    }

    return {};
  };

  const getValidDateProps = (isEndDatePicker: boolean) => {
    if (isEndDatePicker && startDate) {
      const minDate = moment(startDate);
      const maxDate = moment(startDate).add(5, "year");
      return {
        isValidDate: (current: Moment) => {
          return current.isSameOrAfter(minDate, "day") && current.isSameOrBefore(maxDate, "day");
        },
      };
    }

    return {};
  };

  if (!show) return null;

  return (
    <div
      className="width-100pct"
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
    >
      <Card className="padding-25 rounded display-flex flex-column gap-20 width-100pct shadow-default bg-white">
        <div className="display-flex flex-row width-100pct gap-20">
          <div className="form-group flex-1-1 margin-0">
            <label className="text-size-14 text-color-black text-medium" htmlFor="start-date-picker">
              Qual o período inicial?
            </label>
            <div className="input-group">
              <span className="input-group-addon">
                <i className="rioglyph rioglyph-calendar" />
              </span>
              <DatePicker
                timeFormat={false}
                id="start-date-picker"
                dateFormat="DD/MM/YYYY"
                className="width-100pct margin-0"
                inputProps={{ placeholder: "DD/MM/AAAA" }}
                onChange={handleStartDateChange}
                value={startInputValue}
                closeOnClickOutside={false}
                initialViewMode="days"
                locale={DATE_LOCALE}
              />
            </div>
          </div>

          <div className={`form-group flex-1-1 margin-0 ${hasEndDatePickerError ? 'has-feedback has-error' : ''}`}>
            <label className="text-size-14 text-color-black text-medium" htmlFor="end-date-picker">
              Qual o período final?
            </label>
            <div className="input-group">
              <span className="input-group-addon">
                <i className="rioglyph rioglyph-calendar" />
              </span>
              <DatePicker
                timeFormat={false}
                id="end-date-picker"
                dateFormat="DD/MM/YYYY"
                className="width-100pct margin-0"
                inputProps={{ placeholder: "DD/MM/AAAA" }}
                onChange={handleEndDateChange}
                value={endInputValue}
                closeOnClickOutside={false}
                initialViewMode="days"
                locale={DATE_LOCALE}
                {...getValidDateProps(true)}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
